import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect } from "react";
import Select from 'react-select';
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

import Spinner from "../../components/Spinner";
import {
  useCountries,
  useCreateJob,
  useIndustries,
  useJob,
  useUpdateJob,
} from "../../hooks/useJobBoard";

import {
  createJobSchema,
  CreateJobFormValues,
} from "../../utils/jobBoardSchemas";

export default function AdminJobFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const editing = Boolean(id);

  const job = useJob(id);
  const create = useCreateJob();
  const update = useUpdateJob();

  const industries = useIndustries();
  const countries = useCountries();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateJobFormValues>({
    resolver: zodResolver(createJobSchema),
    defaultValues: {
      title: "",
      description: "",
      requirements: "",
      industryIds: [],
      countryIds: [],
      expirationDate: "",
      status: "Draft",
    },
  });

  useEffect(() => {
    if (!job.data) return;

    reset({
      title: job.data.title ?? "",
      description: job.data.description ?? "",
      requirements: job.data.requirements ?? "",
      industryIds: job.data.industryIds ?? [],
      countryIds: job.data.countryIds ?? [],
      expirationDate:
        job.data.expirationDate?.substring(0, 10) ?? "",
      status: job.data.status ?? "Draft",
    });
  }, [job.data, reset]);

  const onSubmit = (values: CreateJobFormValues) => {
    const onSuccess = () =>
      navigate("/admin/job-board/jobs");

    if (editing) {
      update.mutate(
        {
          id: id!,
          data: values,
        },
        {
          onSuccess,
        }
      );
    } else {
      create.mutate(values, {
        onSuccess,
      });
    }
  };

  const saving =
    create.isPending || update.isPending;

  if (editing && job.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "0.5rem",
      borderColor: state.isFocused ? "#8b1538" : "#d1d5db",
      boxShadow: "none",
      backgroundColor: "#ffffff",
      "&:hover": {
        borderColor: "#8b1538",
      },
    }),

    menu: (base: any) => ({
      ...base,
      backgroundColor: "#ffffff",
      zIndex: 50,
    }),

    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#8b1538"
        : state.isFocused
        ? "#f8e8ec"
        : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#1e293b",
      cursor: "pointer",
    }),

    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "#f8e8ec",
    }),

    multiValueLabel: (base: any) => ({
      ...base,
      color: "#8b1538",
      fontWeight: 600,
    }),

    multiValueRemove: (base: any) => ({
      ...base,
      color: "#8b1538",
      ":hover": {
        backgroundColor: "#8b1538",
        color: "#ffffff",
      },
    }),

    placeholder: (base: any) => ({
      ...base,
      color: "#94a3b8",
    }),

    singleValue: (base: any) => ({
      ...base,
      color: "#1e293b",
    }),

    input: (base: any) => ({
      ...base,
      color: "#1e293b",
    }),
  };

  return (
    <div className="space-y-6 text-left max-w-4xl mx-auto">
      <Link
        to="/admin/job-board/jobs"
        className="inline-flex items-center gap-2 text-sm font-semibold text-strivre-mutedtext hover:text-strivre-navy"
      >
        <ArrowLeft size={16} />
        Back to Jobs
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-strivre-navy">
          {editing ? "Edit Job" : "Create Job"}
        </h1>

        <p className="text-sm text-strivre-mutedtext mt-1">
          Publish a clear, searchable opportunity
          for candidates.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white border border-strivre-lightnavy rounded-lg p-6 space-y-5"
      >
        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
            Job Title
          </label>

          <input
            className="input-base"
            {...register("title")}
          />

          {errors.title && (
            <p className="mt-1 text-xs text-red-600">
              {errors.title.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Industry
            </label>

            <Controller
              name="industryIds"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  placeholder="Select industries..."
                  options={
                    industries.data?.map((industry) => ({
                      value: industry.id,
                      label: industry.name,
                    })) ?? []
                  }
                  value={
                    industries.data
                      ?.filter((industry) =>
                        field.value?.includes(industry.id)
                      )
                      .map((industry) => ({
                        value: industry.id,
                        label: industry.name,
                      })) ?? []
                  }
                  onChange={(selected) =>
                    field.onChange(
                      (selected ?? []).map((item) => item.value)
                    )
                  }
                  styles={selectStyles}
                />
              )}
            />

            {errors.industryIds && (
              <p className="mt-1 text-xs text-red-600">
                {errors.industryIds.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Country
            </label>

            <Controller
              name="countryIds"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  placeholder="Select countries..."
                  options={
                    countries.data?.map((country) => ({
                      value: country.id,
                      label: country.name,
                    })) ?? []
                  }
                  value={
                    countries.data
                      ?.filter((country) =>
                        field.value?.includes(country.id)
                      )
                      .map((country) => ({
                        value: country.id,
                        label: country.name,
                      })) ?? []
                  }
                  onChange={(selected) =>
                    field.onChange(
                      (selected ?? []).map((item) => item.value)
                    )
                  }
                  styles={selectStyles}
                />
              )}
            />

            {errors.countryIds && (
              <p className="mt-1 text-xs text-red-600">
                {errors.countryIds.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Expiration Date
            </label>

            <input
              type="date"
              className="input-base"
              {...register("expirationDate")}
            />

            {errors.expirationDate && (
              <p className="mt-1 text-xs text-red-600">
                {errors.expirationDate.message}
              </p>
            )}
          </div>

          {/* <div>
            <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
              Number of People Needed
            </label>

            <input
              type="number"
              min={1}
              className="input-base"
              {...register("numberOfPeopleNeeded", {
                valueAsNumber: true,
              })}
            />

            {errors.numberOfPeopleNeeded && (
              <p className="mt-1 text-xs text-red-600">
                {errors.numberOfPeopleNeeded.message}
              </p>
            )}
          </div> */}
        </div>

        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
            Job Description
          </label>

          <textarea
            rows={8}
            className="input-base"
            {...register("description")}
          />

          {errors.description && (
            <p className="mt-1 text-xs text-red-600">
              {errors.description.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
            Qualifications / Requirements
          </label>

          <textarea
            rows={8}
            className="input-base"
            {...register("requirements")}
          />

          {errors.requirements && (
            <p className="mt-1 text-xs text-red-600">
              {errors.requirements.message}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Link
            to="/admin/job-board/jobs"
            className="btn-outline btn-md rounded-lg"
          >
            Cancel
          </Link>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary btn-md rounded-lg inline-flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2
                  size={16}
                  className="animate-spin"
                />
                Saving...
              </>
            ) : (
              <>
                {editing
                  ? "Save Changes"
                  : "Create Job"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}